import React, { useEffect, useState } from "react";
import { AuthProps } from "../../../Services/AuthService";
import { PermissionSet, User } from "../../../Models/User";
import styles from "./_user-management.module.scss";
import { SideBarSlideOutEditorProps } from "../../Controls/Layout/AppContainer/AppContainer";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../Store/rootReducer";
import { useDrawer } from "../../Providers/DrawerProvider";
import SideDrawer from "../../Controls/Layout/SideDrawer/SideDrawer";
import { 
  // createDpgUser, 
  createUser, loadUsersForClient, loadUsersForClientSuccess, removeDpgUser, removeUser, resendUserInvite, updateUser 
} from "../../../Store/Admin/actions";
import { Client, ColumnInfo, GenericGrid } from "@amsanalytics/react-shared-library";
import { Button, Tooltip, useEventCallback } from "@mui/material";
import { ILookupItem } from "../../../Models/LookupItem";
import UserEditor from "../../Controls/UserEditor/UserEditor";
import { GridCellProps } from "@progress/kendo-react-grid";
import { useDialog } from "../../Providers/DialogProvider";
import ConfirmationDialog from "../../Dialogs/ConfirmationDialog/ConfirmationDialog";
import { displayError, formatPhoneNumber, userHasPermission } from "../../../Helpers/Utilities";
import LoadingPanel from "../../Controls/LoadingPanel/LoadingPanel";
import { ComboBox, ComboBoxChangeEvent, ComboBoxFilterChangeEvent } from "@progress/kendo-react-dropdowns";
import { Dealer } from "../../../Models/Dealer";
import { filterBy, FilterDescriptor } from "@progress/kendo-data-query";
import { permissions } from "../../../Helpers/Constants";
import { PhoneNumberCell } from "../../PhoneNumberGridCell/PhoneNumberGridCell";
export interface UserManagementProps extends AuthProps {
  onOpenSidebar?: (slideoutEditorContent: SideBarSlideOutEditorProps | undefined) => void;
}

export default function UserManagement(props: UserManagementProps) {
  const currentUser: User | null = useSelector((state: AppState) => state.shared.currentUser);
  const users = useSelector<AppState, User[]>(state => state.admin.users.sort((first: User, second: User) => (first.lastName || '').localeCompare(second.lastName || '')));
  const dpgClients = useSelector((state: AppState) => state.library.dpgClients);
  const roles = useSelector<AppState, ILookupItem[]>(state => state.library.roles);
  const userManagementPageLoading = useSelector((state: AppState) => state.shared.userManagementPageLoading);
  const [clientId, setClientId] = useState<string | null>(currentUser && (currentUser.isSuperUser || userHasPermission(currentUser, permissions.users, PermissionSet.U)) ? null: (currentUser?.clientId ?? null));
  const [filteredClients, setFilteredClients] = useState<Dealer[]>(dpgClients);
  const [openDrawer, closeDrawer] = useDrawer();
  const [openDialog, closeDialog] = useDialog();
  const dispatch = useDispatch();
  // When the selected client changes, load the users for that client.
  useEffect(() => {
    if (clientId) {
      dispatch(loadUsersForClient(clientId));    
    }
  }, [currentUser, clientId]);

  useEffect(() => {
    if(dpgClients.length > 0) {
      setFilteredClients(dpgClients);
    }
  }, [dpgClients]);

  useEffect(() => {
    return () => {
      dispatch(loadUsersForClientSuccess([]));
    }
  }, []);

  // Event handler for opening the side drawer.
  const openSideBarSlideOutEditor = (title: string, editor: React.ReactNode, hideCloseButton?: boolean) => {
    openDrawer({
      children: <SideDrawer
        content={editor}
        title={title}
        hideCloseButton={hideCloseButton}
        closeDrawer={closeDrawer}
      />,
      className: 'rightDrawerContainer',
      variant: "temporary",
      anchor: "right"
    });
  }

  // User want to create or edit a user.
  const openUserEditor = (user?: User) => {
    const editingUser = user ? {
      ...user
    } : undefined
    openSideBarSlideOutEditor(
      `${editingUser ? 'Edit' : 'Create'} User`,
      <UserEditor
        user={editingUser}
        adUserIds={users.map(u => u.adUserId)}
        currentUser={currentUser}
        clientId={clientId!}
        roles={roles}
        organizations={[]}
        users={users}
        authService={props.authService}
        onCreateUser={(user: User) => {
          if (clientId) {
            dispatch(createUser({ ...user, clientId: clientId }));
            closeDrawer();
          } else {
            dispatch(displayError('Please select a client before creating a user.'));
          }
        }}
        onUpdateUser={(user: User) => {
          dispatch(updateUser(user))
          closeDrawer();
        }}
        onClose={() => closeDrawer()}
      />,
      false
    );
  };


  const ActionCell = (props: GridCellProps) => {
    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '12px', paddingTop: '1​2px', paddingBottom: '0px', paddingRight: '0px' }}>
        {

          <Tooltip title={`Remove the NIADA user for ${props.dataItem.firstName} ${props.dataItem.lastName}`}>
            <Button
              style={{ padding: '0px', textTransform: 'none' }}
              onClick={(event: React.SyntheticEvent) => {
                event.stopPropagation();
                openDialog({
                  children: (
                    <ConfirmationDialog
                      title='Delete NIADA User'
                      prompt={`Are you sure you want to delete the NIADA user record for ${props.dataItem.firstName} ${props.dataItem.lastName}?`}
                      closeDialog={closeDialog}
                      accept={() => {
                        dispatch(removeDpgUser(props.dataItem.adUserId));
                        closeDialog();
                      }}
                    />
                  ),
                });

              }}
            >
              Remove
            </Button>
          </Tooltip>

        }

      </td>
    );
  };
  const filterClients = (event: ComboBoxFilterChangeEvent) => {
    setFilteredClients(filterBy([...dpgClients], event.filter as FilterDescriptor));
  };
  // Only Super Users can resend or revoke users.
  const columns: ColumnInfo[] =  [
    { title: "First Name", field: "firstName" },
    { title: "Last Name", field: "lastName" },
    { title: "Username", field: "adUserId" },
    { title: "Title", field: "title" },
    { title: "Email", field: "emailAddress" },
    { title: "Mobile", field: "mobilePhone", cell: (props: any) => <PhoneNumberCell {...props} /> },
    { title: "Actions" , cell: (props: any) => <ActionCell {...props} />, preventFiltering: true }
  ];

  return (
    <>
      {currentUser && (
        <div className={styles.userManagementContainer}>
          <GenericGrid<User>
            items={users}
            itemName={"Users"}
            itemNameStyle={{ color: '#d95137' }}
            disableAdding={clientId === null}
            onOpenItemEditor={() => clientId && openUserEditor()}
            onItemSelected={(user: User) => {
              // Super Users can edit anyone; NIADA Users can only edit other NIADA Users
              if (clientId) {
                openUserEditor(user);
              } else {
                dispatch(displayError('Please select a client before creating a user.'))
              }
            }}
            headerComponent={<div style={{display: 'flex', marginLeft: '10px'}}><ComboBox
              data={filteredClients}
              textField="name"
              title="Select a client.."
              filterable={true}
              onFilterChange={filterClients}
              onChange={(event: ComboBoxChangeEvent) => {
                setClientId((event.value as Dealer)?.id ?? null);
              }}
              placeholder="Select dealer ..."
              style={{ width: "300px" }}
            /></div>}
            useIconButtons={true}
            disablePdfExporting={true}
            disableExcelExporting={true}
            disableGrouping={true}
            columns={columns}
            style={{ maxHeight: 'calc(100vh - 60px' }}
          />
        </div>
      )}
      {userManagementPageLoading && <LoadingPanel style={{position: 'absolute'}} label={"Loading Users..."} />}
    </> 
  );
}
